import { _s, getLang } from '@/locale';
import moment from 'moment';

export type Day = {
  date: Date;
  isCurrentMonth: boolean;
  isToday: boolean;
  isWeekend: boolean;
  isSunday: boolean;
  isFirstDayOfCurrentMonth?: boolean; //used for highlighting the first day of the month
  isLastDayOfCurrentMonth?: boolean; // used for highlighting the last day of the month
};

/**
 * Generates an array of weeks for a given month.
 * Each week is an array of Day objects.
 */
export const getWeeksInMonth = (date: Date): Day[][] => {
  const monthStart = moment(date).startOf('month');
  const monthEnd = moment(date).endOf('month');
  const startDate = moment(monthStart).startOf('week');

  if (startDate.day() === 1) {
    // This should take it to Monday of the current week (or previous if it's Sunday of current)
    startDate.day(-6);
  } else {
    startDate.startOf('week').add(1, 'days'); // Move from Sunday to Monday
  }

  const endDate = moment(monthEnd).endOf('week');
  if (endDate.day() === 0) {
    // If Sunday, no change needed for end of week
    // do nothing
  } else {
    endDate.endOf('week').add(1, 'days'); // Move from Saturday to Sunday
  }

  const weeks: Day[][] = [];
  let currentDay = moment(startDate);
  let week: Day[] = [];

  while (currentDay.isSameOrBefore(endDate, 'day')) {
    week.push({
      date: currentDay.toDate(),
      isCurrentMonth: currentDay.isSame(monthStart, 'month'),
      isFirstDayOfCurrentMonth: currentDay.isSame(monthStart, 'day'),
      isLastDayOfCurrentMonth: currentDay.isSame(monthEnd, 'day'),
      isToday: currentDay.isSame(moment(), 'day'),
      isWeekend: currentDay.day() === 0 || currentDay.day() === 6, // Sunday (0) or Saturday (6)
      isSunday: currentDay.day() === 0, // Sunday (0)
    });

    if (week.length === 7) {
      weeks.push(week);
      week = [];
    }
    currentDay.add(1, 'day');
  }

  // add the last week if it's not full
  if (week.length > 0) {
    while (week.length < 7) {
      const lastDateInWeek = moment(week[week.length - 1].date);
      const nextDay = lastDateInWeek.add(1, 'day');
      week.push({
        date: nextDay.toDate(),
        isCurrentMonth: false,
        isToday: false,
        isWeekend: nextDay.day() === 0 || nextDay.day() === 6,
        isSunday: nextDay.day() === 0,
      });
    }
    weeks.push(week);
  }

  return weeks;
};

/**
 * Gets the week number for a given date.
 */
export const getWeekNumber = (date: Date): number => {
  return moment(date).isoWeek();
};

/**
 * Formats a date to 'MMM yyyy' (e.g., 'Jan 2025'). if the year is not the current
 * Formats a date to 'MMM' (e.g., 'Jan') if the year is the current year.
 */
export const formatMonthYear = (date: Date): string => {
  const currentYear = moment().year();
  const yearOfDate = moment(date).year();
  const monthFormat = currentYear === yearOfDate ? 'MMM' : 'MMM YYYY';
  return moment(date).format(monthFormat);
};

/**
 * Checks if a date is within a given range.
 */
export const isDateWithinRange = (date: Date, startDate: Date | null, endDate: Date | null): boolean => {
  if (!startDate || !endDate) return false;

  const mDate = moment(date);
  const mStartDate = moment(startDate);
  const mEndDate = moment(endDate);

  // Ensure start and end are in correct order for interval
  const sortedStart = mStartDate.isSameOrBefore(mEndDate) ? mStartDate : mEndDate;
  const sortedEnd = mStartDate.isSameOrBefore(mEndDate) ? mEndDate : mStartDate;

  // '[]' means inclusive of start and end
  return mDate.isBetween(sortedStart, sortedEnd, 'day', '[]');
};

/**
 * Checks if a date is the start of the selected range.
 */
export const isRangeStart = (date: Date, startDate: Date | null): boolean => {
  return startDate ? moment(date).isSame(startDate, 'day') : false;
};

/**
 * Checks if a date is the end of the selected range.
 */
export const isRangeEnd = (date: Date, endDate: Date | null): boolean => {
  return endDate ? moment(date).isSame(endDate, 'day') : false;
};

export const getInitialMonths = (initialDate: Date, numMonths: number): Date[] => {
  const months: Date[] = [];
  let currentMonth = moment(initialDate);
  for (let i = 0; i < numMonths; i++) {
    months.push(currentMonth.toDate());
    currentMonth.add(1, 'month');
  }
  return months;
};

const monthKeys = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'] as const;

const customMonthShorts: { [key: number]: string } = Object.fromEntries(
  monthKeys.map((key, index) => [
    index,
    _s(key, getLang() === 'sv' || getLang() === 'fi' ? { postProcess: 'lowercase' } : {}),
  ]),
);

const formatSingleDate = (date: Date, currentDate: Date): string => {
  const mDate = moment(date);
  const mCurrentDate = moment(currentDate);

  if (mDate.isSame(mCurrentDate, 'year')) {
    /**
     * Example output: "17 maj"
     */
    return `${mDate.format('D')} ${customMonthShorts[mDate.month()]}`;
  } else {
    /**
     * Example output: "17 maj 2026"
     */
    return `${mDate.format('D')} ${customMonthShorts[mDate.month()]} ${mDate.format('YYYY')}`;
  }
};

/**
 * Formats a date range string based on selected dates.
 *
 * @param selectedDates An array of selected Date objects.
 * @param dateNow The current date (used for year comparison).
 * @returns A formatted date range string, or null if no dates are selected.
 */
export const formatSelectedRange = (selectedDates: Date[], dateNow: Date): string | null => {
  /**
   * If no dates are selected, return the default of todays daye and three weeks forward
   */
  if (selectedDates.length === 0) {
    const startDate = moment(dateNow);
    const endDate = moment(startDate).add(21, 'days'); // 3 weeks forward
    return `${formatSingleDate(startDate.toDate(), dateNow)} - ${formatSingleDate(endDate.toDate(), dateNow)}`;
  }

  const sortedDates = selectedDates.slice().sort((a, b) => moment(a).diff(b)); // Use moment().diff() for sorting

  const startDate = sortedDates[0];
  const endDate = sortedDates[sortedDates.length - 1];

  const mStartDate = moment(startDate);
  const mEndDate = moment(endDate);

  if (mStartDate.isSame(mEndDate, 'day')) {
    return formatSingleDate(startDate, dateNow);
  }

  // Range selection
  const startDay = mStartDate.format('D');
  const endDay = mEndDate.format('D');

  const startMonth = customMonthShorts[mStartDate.month()];
  const endMonth = customMonthShorts[mEndDate.month()];

  if (mStartDate.isSame(mEndDate, 'month')) {
    return `${startDay}-${endDay} ${startMonth}`;
  } else {
    // same year as current year, different month: "28 dec - 1 jan" (assuming current year)
    return `${startDay} ${startMonth} - ${endDay} ${endMonth}`;
  }
};

/**
 * Paginated calendar is used to display two months at a time, when changing month
 * we want to always show the previous and next month in the pair.
 * for exampe: [Jun, Jul] -> [Jul, Aug] -> [Aug, Sep] etc.
 */
export const getOverlappingMonthPairs = (months: Date[]): Date[][] => {
  const pairs = [];
  for (let i = 0; i < months.length - 1; i++) {
    pairs.push(months.slice(i, i + 2));
  }
  return pairs;
};

/**
 * On paginated calendar we want to start on the page that contains the selected start date.
 */
export const findInitialPageIndex = (months: Date[], selectedStart: Date | null): number => {
  if (!selectedStart) return 0;

  const selectedMonth = selectedStart.getMonth();
  const selectedYear = selectedStart.getFullYear();

  const monthIndex = months.findIndex(
    (month) => month.getMonth() === selectedMonth && month.getFullYear() === selectedYear,
  );

  if (monthIndex === -1) return 0;

  const maxStartIndex = months.length - 2;
  return Math.min(Math.max(0, monthIndex), maxStartIndex);
};
